/**
 * Slice a File into ordered part descriptors for S3 multipart upload.
 *
 * Each descriptor carries the 1-based S3 part number, byte offset range,
 * and a Blob slice ready to PUT. The blob is a lazy view onto the underlying
 * File - no copy happens until the slice is read, so this is cheap even for
 * very large files.
 *
 * S3 requires:
 * - Each part >= 5 MB (except the last).
 * - Part numbers in [1, 10000].
 *
 * Callers should default chunkSize to 32 MB; below 5 MB this throws.
 */
export interface FilePart {
  number: number;      // 1-based S3 part number
  start: number;       // inclusive byte offset
  end: number;         // exclusive byte offset
  size: number;        // end - start
  blob: Blob;          // file.slice(start, end)
}

export function chunkFile(file: File, chunkSize: number): FilePart[] {
  if (chunkSize < 5 * 1024 * 1024) {
    throw new Error('chunkSize must be at least 5 MiB (S3 minimum part size)');
  }
  if (file.size === 0) {
    throw new Error('cannot chunk empty file');
  }
  const parts: FilePart[] = [];
  let offset = 0;
  let number = 1;
  while (offset < file.size) {
    if (parts.length >= 10000) {
      throw new Error('file too large for chunkSize: would exceed 10000 parts');
    }
    const end = Math.min(offset + chunkSize, file.size);
    parts.push({ number, start: offset, end, size: end - offset, blob: file.slice(offset, end) });
    offset = end;
    number += 1;
  }
  return parts;
}
